import {ApiClient} from '@/extensions/three_domain_secure/api_client/api-client';
import {
  Gateway,
  PaymentAttemptStatus,
  PaymentIntent,
  PaymentMethodType,
} from '@/extensions/three_domain_secure/common/types';
import {BUSINESS_ENTITY_HEADER} from '@/plugins/core/api/interface';

let apiClient;
let initData;
let pollInterval;
const ALLOWED_CARD_PAYMENT_METHODS = [
  Gateway.ADYEN,
  Gateway.BRAINTREE,
  Gateway.INGENICO_DIRECT,
  Gateway.MOLLIE,
  Gateway.RAZORPAY,
  Gateway.STRIPE,
  Gateway.PAYCOM,
  Gateway.CHECKOUT_COM,
  Gateway.PAYPAL,
  Gateway.DEUTSCHE_BANK,
  Gateway.PAYSTACK,
  Gateway.SOLIDGATE,
];

const POLL_INTERVAL = 3000;
const MAX_HITS = 300;
const MAX_RETRIES = 3;
let retryCount = 0;
let isRequiresChallengeUpdatePosted = false;
let counter = 0;

// Payment intent worker - message handler
onmessage = function (e) {
  const payload = e.data;
  handleAction(payload.action, payload.data);
};

function handleAction(action: string, data: any) {
  if (actionHandlers[action]) {
    actionHandlers[action](data);
    return;
  }
}

// List of actions and handlers
const actionHandlers = {
  initializeWorker: (data) => {
    initData = data;
    apiClient = new ApiClient(initData.site, initData.paymentIntentId, initData.options);
  },

  startPaymentIntentPoll: () => {
    clearInterval(pollInterval);
    counter = 0;
    retryCount = 0;
    isRequiresChallengeUpdatePosted = false;
    pollInterval = setInterval(function () {
      if (counter >= MAX_HITS || retryCount >= MAX_RETRIES) {
        clearInterval(pollInterval);
        timeout();
        return;
      }
      const headers = {
        Authorization: `Bearer ${initData.paymentIntentId}`,
        'X-Requested-With': 'XMLHttpRequest',
      };
      if (initData.businessEntityId) headers[BUSINESS_ENTITY_HEADER] = initData.businessEntityId;
      apiClient.payment_intent
        .retrieve({}, headers)
        .then((_data: any) => {
          if (_data && _data.payment_intent) {
            const intent: PaymentIntent = _data.payment_intent;

            if (!isPiWorkerSupported(intent)) {
              clearInterval(pollInterval);
              return;
            }

            const attempt = intent.active_payment_attempt;

            if (!attempt) {
              return;
            }

            if (attempt.status == PaymentAttemptStatus.REFUSED || attempt.status == PaymentAttemptStatus.AUTHORIZED) {
              _data['version_2'] = true;
              clearInterval(pollInterval);
              return endPaymentIntentPoll(_data);
            }
            if (!isRequiresChallengeUpdatePosted && attempt.status == PaymentAttemptStatus.REQUIRES_CHALLENGE) {
              isRequiresChallengeUpdatePosted = true;
              return postRequiresChallengeUpdate(_data);
            }
          }
        })
        .catch((err) => {
          // TODO: Below code block can be removed after recaptcha validation is removed from backend
          if (
            err.response &&
            err.response.status == 400 &&
            err.response.data &&
            err.response.data.error_code == 'recaptcha_verification_failed'
          ) {
            clearInterval(pollInterval);
            timeout();
            return;
          }

          // Error already tracked in splunk
          retryCount++;
        });
      counter++;
    }, POLL_INTERVAL);
  },
};

function isPiWorkerSupported(intent: PaymentIntent) {
  switch (intent.payment_method_type) {
    case PaymentMethodType.CARD:
      return ALLOWED_CARD_PAYMENT_METHODS.indexOf(intent.gateway) >= 0;
    case PaymentMethodType.DIRECT_DEBIT:
      // Support both GoCardless and Mollie for Direct Debit
      return intent.gateway === Gateway.GOCARDLESS || intent.gateway === Gateway.MOLLIE;
    case PaymentMethodType.IDEAL:
    case PaymentMethodType.SOFORT:
    case PaymentMethodType.KBC_PAYMENT_BUTTON:
    case PaymentMethodType.ELECTRONIC_PAYMENT_STANDARD:
    case PaymentMethodType.TRUSTLY:
    case PaymentMethodType.PAY_BY_BANK:
    case PaymentMethodType.CASH_APP_PAY:
    case PaymentMethodType.ALIPAY:
    case PaymentMethodType.STABLECOIN:
    case PaymentMethodType.BANCONTACT:
    case PaymentMethodType.PAYCONIQ_BY_BANCONTACT:
    case PaymentMethodType.GIROPAY:
    case PaymentMethodType.DOTPAY:
    case PaymentMethodType.Netbanking_EMANDATES:
    case PaymentMethodType.UPI:
    case PaymentMethodType.FASTER_PAYMENTS:
    case PaymentMethodType.PAY_TO:
    case PaymentMethodType.SEPA_INSTANT_TRANSFER:
    case PaymentMethodType.ONLINE_BANKING_POLAND:
    case PaymentMethodType.AMAZON_PAYMENTS:
    case PaymentMethodType.GOOGLE_PAY:
    case PaymentMethodType.APPLE_PAY:
    case PaymentMethodType.KAKAO_PAY:
    case PaymentMethodType.NAVER_PAY:
    case PaymentMethodType.REVOLUT_PAY:
      return true;
    default:
      return false;
  }
}

function endPaymentIntentPoll(data: any) {
  const body = {
    action: 'paymentIntentResult',
    data,
  };
  // @ts-ignore
  postMessage(body);
}

function postRequiresChallengeUpdate(data: any) {
  const body = {
    action: 'paymentIntentRequiresChallenge',
    data,
  };
  // @ts-ignore
  postMessage(body);
}

function timeout() {
  const body = {
    action: 'paymentIntentPollTimeout',
  };
  // @ts-ignore
  postMessage(body);
}
